(function () {

    'use strict';

    if (typeof window === 'undefined') {

        module.exports = exportFn(
            require('./enums/dataSourceIds.js'),
            require('./enums/dataSourceFilterGroupIds.js'),
            require('./enums/metricTransformIds.js'),
            require('./enums/dimensionReducerIds.js'),
            require('./dataSourceFilterGroups.js'),
            require('./dataSources.js'),
            require('../../enums/tweetTypes.js'),
            require('../../enums/likelyPromoted.js'),
            require('../../enums/benchmarkCompaniesFilters.js').benchmarkCompaniesFilters,
            require('object-hash'),
            require('lodash')
        );

    } else {
        RivalIQ.module('metrics.new', function (metricsNew) {
            metricsNew.MetricRequest = exportFn(
                RivalIQ.metrics.new.enums.DataSourceIds,
                RivalIQ.metrics.new.enums.DataSourceFilterGroupIds,
                RivalIQ.metrics.new.enums.MetricTransformIds,
                RivalIQ.metrics.new.enums.DimensionReducerIds,
                RivalIQ.metrics.new.DataSourceFilterGroups,
                RivalIQ.metrics.new.DataSources,
                RivalIQ.enums.tweetTypes,
                RivalIQ.enums.likelyPromoted,
                RivalIQ.utils.benchmarkCompaniesFilters,
                window.object_hash,
                window._
            );
        });
    }

    // TODO:
    // complex measures (e.g., compare period, benchmark metrics)
    // Dimension-specific measures (e.g., company name from companies data source)
    // validate dimension reductions work for the dimension/measure combination
    function exportFn(DataSourceIds, DataSourceFilterGroupIds, MetricTransformIds, DimensionReducerIds, DataSourceFilterGroups, DataSources, tweetTypes, likelyPromoted, benchmarkCompaniesFilters, objectHash, _) {

        const MetricRequestRequiredFields = [
            'dataSourceId',
            'dimensions',
            'measure',
            'filters'
        ];

        const MetricRequestOptionalFields = [
            'dimensionReductions'
        ];

        const AllValidMetricRequestFields = _.keyBy(MetricRequestRequiredFields.concat(MetricRequestOptionalFields));

        const privateKeyPrefix = '___';

        function validateMetricRequest(metricRequest) {
            let errors = [];

            if (!_.isObject(metricRequest)) {
                errors.push('metricRequest must be an object');
                return errors;
            }

            if (!metricRequest.dataSourceId || !DataSourceIds[metricRequest.dataSourceId]) {
                errors = errors.concat([`unknown dataSource: ${metricRequest.dataSourceId}`]);
                return errors; // not much more we can validate without a data source.
            }

            // Canonicalize assumes at least a valid data source, so we have to wait to check at least that before we can validate the rest.
            metricRequest = canonicalizeMetricRequest(metricRequest);

            const dataSource = DataSources[metricRequest.dataSourceId];

            // Ensure all the top-level fields are presence on request.
            errors = _.reduce(MetricRequestRequiredFields, (memo, requiredField) => {
                if (!_.has(metricRequest, requiredField)) {
                    memo = memo.concat([`request missing required field ${requiredField}`]);
                }
                return memo;
            }, errors);


            // Ensure this dataSource can support all the requested dimensions.
            const dataSourceAllDimensions = _.union([], dataSource.storageDimensions, dataSource.availableDimensions);
            _.each(metricRequest.___orderedDimensions, dimension => {
                if (!_.includes(dataSourceAllDimensions, dimension)) {
                    errors = errors.concat([`dimension ${dimension} not allowed in ${metricRequest.dataSourceId}`]);
                }
            });

            const transformDimensions = _getTransformDimensions(metricRequest);
            _.each(transformDimensions, dimension => {
                if (!_.includes(dataSourceAllDimensions, dimension)) {
                    errors = errors.concat([`dimension ${dimension} not allowed in ${metricRequest.dataSourceId}`]);
                }
            });

            // Ensure that every storage dimension is to be dealt with - either it's part of the free dimensions or it's reduced away.
            const storageDimensionsNotRequested = _.difference(dataSource.storageDimensions, _.union(metricRequest.dimensions, transformDimensions));
            _.each(storageDimensionsNotRequested, dimension => {
                const reducer = _.find(metricRequest.___expandedDimensionReductions, {
                    dimension: dimension
                });

                if (!reducer) {
                    errors = errors.concat([`storage dimension ${dimension} needs to be a free dimension or have a reducer defined.`]);
                }
            });

            // Ensure that dimensions are not double counted.
            const requestAllDimensions = (metricRequest.dimensions || []).concat(_.map(metricRequest.___expandedDimensionReductions, 'dimension')).concat(transformDimensions);
            const requestAllDimensionsCount = _.countBy(requestAllDimensions);
            _.each(requestAllDimensionsCount, (count, dimension) => {
                if (count > 1) {
                    errors = errors.concat([`dimension ${dimension} is duplicated`]);
                }
            });

            // Ensure that all dimension reductions are valid.
            _.each(metricRequest.___expandedDimensionReductions, reducer => {
                if (!DimensionReducerIds[reducer.reducer]) {
                    errors = errors.concat([`unknown dimensionReducerId: ${reducer.reducer}`]);
                }
            });

            // Ensure that the measure is valid for this dataSource.
            const isDimensionBasedReducer = _.last(metricRequest.___expandedDimensionReductions)?.reducer === 'distinctCount';
            if (isDimensionBasedReducer) {
                // If the measure is a dimension-based reducer, then the field must be a dimension.
                const allDimensions = dataSource.availableDimensions.concat(dataSource.storageDimensions);
                if (!_.includes(allDimensions, metricRequest.measure.field)) {
                    errors = errors.concat([`measure ${metricRequest.measure.field} should be a dimension in ${metricRequest.dataSourceId}`]);
                }
            } else if (metricRequest.measure?.field && !_.get(dataSource.fields, [metricRequest.measure.field])) {
                errors = errors.concat([`measure ${metricRequest.measure.field} not allowed in ${metricRequest.dataSourceId}`]);
            }

            // If the measure has a transform on it, make sure it's valid.
            if (metricRequest.measure?.transform) {
                switch (metricRequest.measure.transform) {
                    case MetricTransformIds.deltaAbsolute:
                    case MetricTransformIds.deltaRelative:

                        if (!metricRequest.measure.transformConfig?.comparePeriodStartDate || !metricRequest.measure.transformConfig?.comparePeriodEndDate) {
                            errors = errors.concat([`${MetricTransformIds.deltaAbsolute} transform config requires comparePeriodStartDate and comparePeriodEndDate`]);
                        }
                        break;

                    case MetricTransformIds.companiesBenchmark:
                    case MetricTransformIds.companiesBenchmarkDeltaAbsolute:
                    case MetricTransformIds.companiesBenchmarkDeltaRelative:
                        if (_.includes(metricRequest.dimensions, 'companyId')) {
                            errors = errors.concat([`${metricRequest.measure.transform} does not make sense with companyId as a free dimension`]);
                        }

                        if (!DimensionReducerIds[metricRequest.measure.transformConfig?.reducer]) {
                            errors = errors.concat([`${metricRequest.measure.transformConfig?.reducer} is not a valid reducer for ${metricRequest.measure.transform}`]);
                        }

                        if (!benchmarkCompaniesFilters[metricRequest.measure.transformConfig?.benchmarkCompaniesFilter]) {
                            errors = errors.concat([`${metricRequest.measure.transformConfig?.benchmarkCompaniesFilter} is not a valid benchmarkCompaniesFilter for ${metricRequest.measure.transform}`]);
                        }

                        break;

                    case MetricTransformIds.percentOfTotal:
                        if (_.isEmpty(metricRequest.dimensions)) {
                            errors = errors.concat([`${metricRequest.measure.transform} requires at least one free dimension`]);
                        }
                        break;

                    default:
                        errors = errors.concat([`unknown transform ${metricRequest.measure.transform}`]);
                }
            }

            // if the metric has a ___returns directive, make sure it's supported and valid.
            if (metricRequest.___returns) {
                if (metricRequest.___returns.operator === 'dimensionValue') {
                    const dimension = metricRequest.___returns.value;
                    if (!_.includes(metricRequest.dimensions, dimension)) {
                        errors = errors.concat([`returns dimension ${dimension} is not a free dimension on this metric`]);
                    }
                } else {
                    errors = errors.concat([`${metricRequest.___returns.operator} is not supported for "returns"`]);
                }
            }

            // Ensure that the requested filters are all supported by this dataSource.
            const supportedFilterGroups = dataSource.supportedFilterGroups;
            _.each(metricRequest.filters, (filters, filterGroupId) => {
                if (!_.includes(supportedFilterGroups, filterGroupId)) {
                    errors = errors.concat([`filter group ${filterGroupId} not allowed in ${metricRequest.dataSourceId}`]);
                } else {
                    const dataSourceFilter = DataSourceFilterGroups[filterGroupId];
                    if (!dataSourceFilter) {
                        errors = errors.concat([`unknown filter group ${filterGroupId}`]);
                    } else {
                        _.each(filters, (filterValue, filterName) => {
                            if (!dataSourceFilter[filterName]) {
                                errors = errors.concat([`${filterName} is not valid for filter group ${filterGroupId}`]);
                            }
                        });
                    }
                }
            });

            // Rather than defaulting to tweetType = normal, require tweetType to be set for Cross channel and Twitter post data sources.
            if (_.includes([DataSourceIds.CROSS_CHANNEL_PUBLIC_POST_DATA, DataSourceIds.TWITTER_PUBLIC_POST_DATA], metricRequest.dataSourceId)) {
                const filterTweetTypes = _.get(metricRequest, ['filters', DataSourceFilterGroupIds.publicPost, 'tweetTypes']);
                if (!filterTweetTypes) {
                    errors = errors.concat([`tweetTypes filter must be specified for metrics in dataSource ${metricRequest.dataSourceId}`]);
                } else {
                    // make sure the tweetTypes are valid.
                    const invalidTweetTypes = _.filter(filterTweetTypes, tweetType => !tweetTypes[tweetType]);
                    if (!_.isEmpty(invalidTweetTypes)) {
                        errors = errors.concat([`tweetTypes ${invalidTweetTypes.join(', ')} is not valid for dataSource ${metricRequest.dataSourceId}`]);
                    }
                }
            }

            // If this data source supports timePeriod, make sure it's filled out.
            if (_.includes(supportedFilterGroups, DataSourceFilterGroupIds.timePeriod)) {
                const timePeriod = _.get(metricRequest, ['filters', DataSourceFilterGroupIds.timePeriod]);
                if (!timePeriod || !timePeriod.startDate || !timePeriod.endDate) {
                    errors = errors.concat([`timePeriod filter must be specified for metrics in dataSource ${metricRequest.dataSourceId}`]);
                }
            }

            // Ensure all the other fields on the request object are valid (i.e., don't allow extra fields).
            _.forIn(metricRequest, (value, key) => {
                if (!AllValidMetricRequestFields[key] && !key.startsWith(privateKeyPrefix)) {
                    errors = errors.concat([`unknown field: ${key}`]);
                }
            });

            if (!_.isEmpty(errors)) {
                return errors;
            }
        }

        const INLINE_DIRECTIVE_RE = /^([\w]+)\((.+)\)$/;
        function _expandMetricReturnsDirective(metricRequest) {
            const returns = metricRequest.measure?.returns;

            if (returns) {
                const match = INLINE_DIRECTIVE_RE.exec(returns);
                if (match) {
                    metricRequest.___returns = {
                        operator: match[1],
                        value: match[2]
                    };
                }
            }
        }

        function _expandInlineDimensionReduction(metricRequest) {

            let measure = _.get(metricRequest, ['measure', 'field'], '');
            const expansions = [];

            while (true) {
                const nextExpansionMatch = INLINE_DIRECTIVE_RE.exec(measure);
                if (!nextExpansionMatch) {
                    break;
                }

                expansions.push({
                    reducer: nextExpansionMatch[1]
                });

                measure = nextExpansionMatch[2];
            }

            if (expansions.length > 1) {
                // In theory it's possible to have multiple inline reductions, but only support
                // one for now as more than that is melting my brain.
                throw new Error('only one inline dimension reduction is supported');
            }

            if (expansions.length === 1) {
                const dataSource = DataSources[metricRequest.dataSourceId];
                if (!dataSource) {
                    throw new Error(`cannot expand inline dimensions with unknown dataSource: ${metricRequest.dataSourceId}`);
                }

                const transformDimensions = _getTransformDimensions(metricRequest);
                const usedDimensions = _.union(_.map(metricRequest.___expandedDimensionReductions, 'dimension'), metricRequest.dimensions, transformDimensions);
                const availableFreeDimensions = _.difference(dataSource?.storageDimensions, usedDimensions);
                const dimensionToReduce = _.first(availableFreeDimensions);
                if (!dimensionToReduce) {
                    throw new Error(`cannot expand inline dimensions with no available dimensions to expand`);
                }

                metricRequest.dimensionReductions.push({
                    [dimensionToReduce]: expansions[0].reducer
                });

                metricRequest.___expandedDimensionReductions.push({
                    dimension: dimensionToReduce,
                    reducer: expansions[0].reducer
                });

                metricRequest.measure.field = measure;
            }

        }

        function _getTransformDimensions(request) {
            switch (request?.measure?.transform) {
                case MetricTransformIds.companiesBenchmark:
                case MetricTransformIds.companiesBenchmarkDeltaAbsolute:
                case MetricTransformIds.companiesBenchmarkDeltaRelative:
                    return ['companyId'];

                default:
                    return [];
            }
        }

        function _applyFilterGroupSpecificNormalizations(request) {
            if (request.filters) {
                if (request.filters[DataSourceFilterGroupIds.company]) {
                    const companyIds = request.filters[DataSourceFilterGroupIds.company].companyIds;
                    if (_.isArray(companyIds)) {
                        request.filters[DataSourceFilterGroupIds.company].companyIds = companyIds.sort();
                    }

                    // companyFilter of null means match all companies.
                    const companyFilter = request.filters[DataSourceFilterGroupIds.company].companyFilter || null;
                    request.filters[DataSourceFilterGroupIds.company].companyFilter = companyFilter;
                }

                if (request.filters[DataSourceFilterGroupIds.timePeriod]) {
                    request.filters[DataSourceFilterGroupIds.timePeriod].timezone ||= 'UTC';
                }

                if (request.filters[DataSourceFilterGroupIds.listening]) {
                    const searchTermsAnd = request.filters[DataSourceFilterGroupIds.listening].searchTermsAnd;
                    if (!_.isEmpty(searchTermsAnd)) {

                        if (!_.isArray(searchTermsAnd)) {
                            throw new Error(`searchTermsAnd must be an array`);
                        }

                        // compact the search terms array.
                        request.filters[DataSourceFilterGroupIds.listening].searchTermsAnd = searchTermsAnd.filter(termsArray => termsArray?.length);
                    }

                    // Remove any empty terms (improves cache utilization since `searchTermsNot=[]` is the same as `searchTermsNot=undefined`).
                    _.each(request.filters[DataSourceFilterGroupIds.listening], (value, key) => {
                        if (_.isNull(value) || _.isUndefined(value) || (_.isArray(value) && _.isEmpty(value))) {
                            delete request.filters[DataSourceFilterGroupIds.listening][key];
                        }
                    });

                    // Remove any empty topic types (improves cache utilization since `listeningQueryTypes=[]` is the same as `listeningQueryTypes=undefined`).
                    const listeningQueryTypes = request.filters[DataSourceFilterGroupIds.listening].listeningQueryTypes;
                    if (_.isEmpty(listeningQueryTypes)) {
                        delete request.filters[DataSourceFilterGroupIds.listening].listeningQueryTypes;
                    } else {
                        request.filters[DataSourceFilterGroupIds.listening].listeningQueryTypes = _.compact(listeningQueryTypes).sort();
                    }
                }
            }

            const supportedFilterGroups = _.get(DataSources, [request.dataSourceId, 'supportedFilterGroups']);
            if (_.includes(supportedFilterGroups, DataSourceFilterGroupIds.publicPost)) {
                request.filters ||= {};
                request.filters[DataSourceFilterGroupIds.publicPost] ||= {};

                if (request.filters[DataSourceFilterGroupIds.publicPost].searchTerm && !_.isString(request.filters[DataSourceFilterGroupIds.publicPost].searchTerm)) {
                    request.filters[DataSourceFilterGroupIds.publicPost].searchTerm = String(request.filters[DataSourceFilterGroupIds.publicPost].searchTerm);
                }
                request.filters[DataSourceFilterGroupIds.publicPost].promotedPosts ||= [likelyPromoted.values.likelyPromoted, likelyPromoted.values.notLikelyPromoted];

                request.filters[DataSourceFilterGroupIds.publicPost].minDistinctCompanyIds ||= 0;
                request.filters[DataSourceFilterGroupIds.publicPost].minDistinctPostIds ||= 0;

                request.filters[DataSourceFilterGroupIds.publicPost].postTagConjunction ||= 'OR';
                request.filters[DataSourceFilterGroupIds.publicPost].postTagExclude ||= false;

                request.filters[DataSourceFilterGroupIds.publicPost].elideZeroPostsCompanies ||= false;
            }
        }

        function canonicalizeMetricRequest(metricRequest) {
            if (metricRequest.___isCanonical) {
                return metricRequest;
            }

            const canonicalized = _.cloneDeep(metricRequest);

            // Serialiing metrics as a query string might result in an empty dimensions array getting dropped; this handles that case.
            canonicalized.dimensions ||= [];

            if (_.isString(canonicalized.measure)) {
                canonicalized.measure = {
                    field: canonicalized.measure
                };
            }

            _expandMetricReturnsDirective(canonicalized);

            canonicalized.dimensionReductions = canonicalized.dimensionReductions || [];
            canonicalized.___expandedDimensionReductions = _.map(canonicalized.dimensionReductions, dimensionReduction => {
                const dimension = _.keys(dimensionReduction)[0];
                const reduction = {
                    dimension,
                    reducer: dimensionReduction[dimension]
                };

                if (dimensionReduction.dimensionValue) {
                    reduction.dimensionValue = dimensionReduction.dimensionValue;
                }

                return reduction;
            });

            _expandInlineDimensionReduction(canonicalized);

            // If there's no transform, make sure there's no transformConfig either (so that objecthashes match).
            if (canonicalized.measure) {
                if (!canonicalized.measure.transform) {
                    delete canonicalized.measure.transformConfig;
                } else {
                    canonicalized.measure.transformConfig = canonicalized.measure.transformConfig || {};
                }
            }

            const dimensionReductionDimensions = _.map(canonicalized.___expandedDimensionReductions, 'dimension');
            canonicalized.___orderedDimensions = _.union(metricRequest.dimensions, dimensionReductionDimensions);

            _applyFilterGroupSpecificNormalizations(canonicalized);

            canonicalized.___isCanonical = true;
            canonicalized.___uuid = objectHash(_.omit(canonicalized, '___requestGroup'));
            Object.freeze(canonicalized);

            return canonicalized;
        }

        // returns an array of pairs. First of each pair is an object with a specific
        // set of values of keys, second is the requests with those values exactly, keyed by UUID.
        // This is based on the other groupRequestsByKeys, which returns an array instead of the UUID keyed requests.
        function groupRequestsByKeys(requestsByUUID, keys) {

            const groupedRequests = [];
            const groupedRequestByKey = {};

            _.each(requestsByUUID, (request, uuid) => {
                const keyValues = _.reduce(keys, (memo, key, idx) => {
                    if (_.isObject(key)) {
                        memo[_.keys(key)[0]] = _.values(key)[0](request);
                    } else {
                        // NOTE: intentionally using the string form of get here as key might be a path.
                        _.set(memo, key, _.get(request, key));
                    }
                    return memo;
                }, {});
                const hash = objectHash(keyValues);
                if (!groupedRequestByKey[hash]) {
                    groupedRequestByKey[hash] = {
                        key: keyValues,
                        requestsByUUID: {}
                    };
                    groupedRequests.push(groupedRequestByKey[hash]);
                }

                groupedRequestByKey[hash].requestsByUUID[uuid] = request;
            });

            return groupedRequests;
        }

        function uniqueKeyForMetricRequest(metricRequest) {

            metricRequest = canonicalizeMetricRequest(metricRequest);
            return metricRequest.___uuid;
        }

        function extendMetricRequest(metricRequest, extensions) {
            const extendedMetricRequest = _.extend({}, metricRequest, extensions);

            _.each(extendedMetricRequest, (value, key) => {
                if (key.startsWith(privateKeyPrefix)) {
                    delete extendedMetricRequest[key];
                }
            });

            return extendedMetricRequest;
        }

        function serialize(metricRequest) {
            metricRequest = extendMetricRequest(metricRequest);
            return metricRequest;
        }

        function isDeltaAbsoluteRequest(metricRequest) {
            const transform = metricRequest?.measure?.transform;

            return _.includes([MetricTransformIds.deltaAbsolute, MetricTransformIds.companiesBenchmarkDeltaAbsolute], transform);
        }

        function isDeltaRelativeRequest(metricRequest) {
            const transform = metricRequest?.measure?.transform;

            return _.includes([MetricTransformIds.deltaRelative, MetricTransformIds.companiesBenchmarkDeltaRelative], transform);
        }

        function getTimeDimension(metricRequest) {
            return _.intersection(metricRequest.dimensions, ['hour', 'day', 'week', 'month'])?.[0];
        }

        return {
            validateMetricRequest: validateMetricRequest,
            canonicalizeMetricRequest: canonicalizeMetricRequest,
            groupRequestsByKeys: groupRequestsByKeys,
            uniqueKeyForMetricRequest: uniqueKeyForMetricRequest,
            extendMetricRequest: extendMetricRequest,
            serialize: serialize,
            expandInlineDimensionReduction: _expandInlineDimensionReduction,
            isDeltaAbsoluteRequest: isDeltaAbsoluteRequest,
            isDeltaRelativeRequest: isDeltaRelativeRequest,
            getTimeDimension: getTimeDimension
        };
    }
})();
